import { AUDIT_ACTIONS } from '../audit/retention';
import { fill, t, type StringKey } from '../strings';

/**
 * 🔒 The `deal.*` audit family (piece 18; decisions 1301 and 1351): one row on
 * the Deal for each change a person makes to its contacts, team, authority list,
 * lists, Budget or Timing, and to its own fields and stage. The row's `changedFields?` names what it touched under the
 * fields below, and the History tile reads this module's sentence for it.
 */
export const DEAL_HISTORY_FIELDS = {
  /** The person's name at the time of the change, as the row recorded it. */
  person: 'person',
  /** The role in words: a Deal Team role's name, or the authority part's label. */
  role: 'role',
  /** The list's own name. */
  bom: 'bom',
  /** A team row's share: recorded as moved, never with the figures (decision 0125). */
  share: 'commissionSharePercent',
  /** An authority row's note. */
  note: 'note',
  /** The Deal's own fields (decision 1351), each recorded as the words a person read at the time. */
  name: 'name',
  value: 'dealValue',
  account: 'account',
  dealType: 'dealType',
  closeDate: 'expectedCloseDate',
  /** 🔒 The Service Request tile's Edit (decision 12600): the tier by name, the window as read, the ceiling as read. */
  requestTier: 'requestTier',
  requestWindow: 'requestWindow',
  requestCeiling: 'requestCeiling',
  /** 🔒 Name the Owner on a request that waited for one (decision 1992): the owner before and after, by name. */
  owner: 'owner',
  stage: 'stage',
  lossReason: 'lostReason',
  /** 🔒 A clone's entry on the Deal cloned from (decision 1482): the new Deal's id, and its name as it was made. */
  clonedDealId: 'clonedDealId',
  clonedDealName: 'clonedDealName',
  /** 🔒 A clone's own creation row (decision 1487): the Deal it was cloned from, by id and by its name as it stood. */
  sourceDealId: 'sourceDealId',
  sourceDealName: 'sourceDealName',
} as const;

/** 🔒 The order a `deal.edited` row's clauses read in, and the sentence each reads (decision 1351). */
const EDIT_CLAUSES: readonly (readonly [string, StringKey])[] = [
  [DEAL_HISTORY_FIELDS.name, 'history.audit.dealNameChanged'],
  [DEAL_HISTORY_FIELDS.value, 'history.audit.dealValueChanged'],
  [DEAL_HISTORY_FIELDS.account, 'history.audit.dealAccountChanged'],
  [DEAL_HISTORY_FIELDS.dealType, 'history.audit.dealTypeChanged'],
  [DEAL_HISTORY_FIELDS.closeDate, 'history.audit.dealCloseDateChanged'],
  [DEAL_HISTORY_FIELDS.owner, 'history.audit.dealOwnerNamed'],
  [DEAL_HISTORY_FIELDS.requestTier, 'history.audit.dealRequestTierChanged'],
  [DEAL_HISTORY_FIELDS.requestWindow, 'history.audit.dealRequestWindowChanged'],
  [DEAL_HISTORY_FIELDS.requestCeiling, 'history.audit.dealRequestCeilingChanged'],
];

export const DEAL_HISTORY_ACTIONS: readonly string[] = [
  AUDIT_ACTIONS.dealContactAdded, AUDIT_ACTIONS.dealContactEnded, AUDIT_ACTIONS.dealContactMadePrimary,
  AUDIT_ACTIONS.dealTeamAdded, AUDIT_ACTIONS.dealTeamEnded, AUDIT_ACTIONS.dealTeamChanged,
  AUDIT_ACTIONS.dealAuthorityAdded, AUDIT_ACTIONS.dealAuthorityChanged, AUDIT_ACTIONS.dealAuthorityRemoved,
  AUDIT_ACTIONS.dealBomAttached, AUDIT_ACTIONS.dealBomDetached, AUDIT_ACTIONS.dealBudgetSaved, AUDIT_ACTIONS.dealTimingSaved,
  AUDIT_ACTIONS.dealEdited, AUDIT_ACTIONS.dealStageMoved, AUDIT_ACTIONS.dealStageWon, AUDIT_ACTIONS.dealStageLost, AUDIT_ACTIONS.dealStageReopened,
  AUDIT_ACTIONS.dealCloned, AUDIT_ACTIONS.dealCreated,
];

/** One `{field, from, to}` entry, the shape piece 9 declares for `changedFields?`. */
export interface DealHistoryChange {
  field: string;
  from: unknown;
  to: unknown;
}

const LINES: Readonly<Record<string, StringKey>> = {
  [AUDIT_ACTIONS.dealContactAdded]: 'history.audit.dealContactAdded',
  [AUDIT_ACTIONS.dealContactEnded]: 'history.audit.dealContactEnded',
  [AUDIT_ACTIONS.dealContactMadePrimary]: 'history.audit.dealContactMadePrimary',
  [AUDIT_ACTIONS.dealTeamAdded]: 'history.audit.dealTeamAdded',
  [AUDIT_ACTIONS.dealTeamEnded]: 'history.audit.dealTeamEnded',
  [AUDIT_ACTIONS.dealAuthorityAdded]: 'history.audit.dealAuthorityAdded',
  [AUDIT_ACTIONS.dealAuthorityRemoved]: 'history.audit.dealAuthorityRemoved',
  [AUDIT_ACTIONS.dealBomAttached]: 'history.audit.dealBomAttached',
  [AUDIT_ACTIONS.dealBomDetached]: 'history.audit.dealBomDetached',
  [AUDIT_ACTIONS.dealBudgetSaved]: 'history.audit.dealBudgetSaved',
  [AUDIT_ACTIONS.dealTimingSaved]: 'history.audit.dealTimingSaved',
  [AUDIT_ACTIONS.dealStageMoved]: 'history.audit.dealStageMoved',
  [AUDIT_ACTIONS.dealStageWon]: 'history.audit.dealStageWon',
  [AUDIT_ACTIONS.dealStageReopened]: 'history.audit.dealStageReopened',
  [AUDIT_ACTIONS.dealCloned]: 'history.audit.dealCloned',
  [AUDIT_ACTIONS.dealCreated]: 'history.audit.dealCreated',
};

function entries(changedFields: unknown): DealHistoryChange[] {
  return Array.isArray(changedFields) ? changedFields.filter((f): f is DealHistoryChange => typeof f === 'object' && f !== null && typeof (f as { field?: unknown }).field === 'string') : [];
}

/** The named field's words: its new value, else its old one, else null. */
function named(changes: readonly DealHistoryChange[], field: string): string | null {
  const change = changes.find((c) => c.field === field);
  if (!change) return null;
  const value = change.to ?? change.from;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** A recorded value as words, or null where the row carried none. */
function words(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * 🔒 A `deal.edited` row as one sentence (decision 1351): a clause for each
 * field it moved, in a fixed order, joined as a person writes a list. A row
 * naming no field it knows reads that the Deal was edited.
 */
function editedLine(changes: readonly DealHistoryChange[]): string {
  const clauses: string[] = [];
  for (const [field, key] of EDIT_CLAUSES) {
    const change = changes.find((c) => c.field === field);
    if (!change) continue;
    clauses.push(fill(t(key), { from: words(change.from) ?? '—', to: words(change.to) ?? '—' }));
  }
  if (clauses.length === 0) return t('history.audit.dealEdited');
  const [first, ...rest] = clauses.map((clause, at) => (at === 0 ? clause : `${clause.charAt(0).toLowerCase()}${clause.slice(1)}`));
  if (rest.length === 0) return first!;
  const last = rest.pop()!;
  return `${[first, ...rest].join(', ')}, ${t('history.audit.and')} ${last}`;
}

/** 🔒 A `deal.stage.lost` row reads the reason recorded with it, where there was one. */
function lostLine(changes: readonly DealHistoryChange[]): string {
  const reason = words(changes.find((c) => c.field === DEAL_HISTORY_FIELDS.lossReason)?.to);
  return reason ? fill(t('history.audit.dealStageLost'), { reason }) : t('history.audit.dealStageLostNoReason');
}

/**
 * The History sentence for a `deal.*` row, or null for any other action. A
 * change row reads by which of its fields moved: a team row's role, its share
 * or both, and an authority row's part or only its note.
 */
export function dealHistoryLine(action: string, changedFields: unknown): string | null {
  if (!DEAL_HISTORY_ACTIONS.includes(action)) return null;
  const changes = entries(changedFields);
  if (action === AUDIT_ACTIONS.dealEdited) return editedLine(changes);
  if (action === AUDIT_ACTIONS.dealStageLost) return lostLine(changes);
  const stage = changes.find((c) => c.field === DEAL_HISTORY_FIELDS.stage);
  const values = {
    person: named(changes, DEAL_HISTORY_FIELDS.person) ?? t('history.audit.dealSomebody'),
    role: named(changes, DEAL_HISTORY_FIELDS.role) ?? '',
    bom: named(changes, DEAL_HISTORY_FIELDS.bom) ?? '',
    deal: named(changes, DEAL_HISTORY_FIELDS.clonedDealName) ?? named(changes, DEAL_HISTORY_FIELDS.sourceDealName) ?? '',
    from: words(stage?.from) ?? '—',
    to: words(stage?.to) ?? '—',
  };
  const moved = (field: string) => changes.some((c) => c.field === field);
  let key = LINES[action];
  // 🔒 A clone's creation row names the Deal it came from; any other creation row names the person alone (decision 1487).
  if (action === AUDIT_ACTIONS.dealCreated && moved(DEAL_HISTORY_FIELDS.sourceDealName)) key = 'history.audit.dealCreatedFromClone';
  if (action === AUDIT_ACTIONS.dealTeamChanged) {
    const role = moved(DEAL_HISTORY_FIELDS.role);
    const share = moved(DEAL_HISTORY_FIELDS.share);
    key = role && share ? 'history.audit.dealTeamRoleAndShareChanged' : role ? 'history.audit.dealTeamRoleChanged' : 'history.audit.dealTeamShareChanged';
  }
  if (action === AUDIT_ACTIONS.dealAuthorityChanged) {
    key = moved(DEAL_HISTORY_FIELDS.role) ? 'history.audit.dealAuthorityRoleChanged' : 'history.audit.dealAuthorityNoteChanged';
  }
  return key ? fill(t(key), values) : null;
}
